import React, { useState } from 'react';
import { Zap, Database, ArrowRight, ArrowLeft, Plus, X, CheckCircle2, AlertCircle } from 'lucide-react';

// 模拟从昨晚内部工作台传递过来的真实社媒提纯数据
const MOCK_SOCIAL_DATA = {
  categories: [
    { id: 'c1', name: '家用除螨仪', source: 'xiaohongshu', weight: 98 },
    { id: 'c2', name: '宠物家庭吸尘器', source: 'baidu', weight: 85 },
  ],
  needs: [
    { id: 'n1', name: '孕妇可用无辐射', source: 'xiaohongshu', weight: 95 },
    { id: 'n2', name: '毛发防缠绕', source: 'baidu', weight: 92 },
    { id: 'n3', name: '静音不吵猫咪', source: 'xiaohongshu', weight: 88 },
  ]
};

// 基础维度弹药库（初始状态，由AI常识生成）
const INITIAL_ARSENAL = {
  categories: [{ id: 'base-c1', name: '吸尘器' }],
  needs: [{ id: 'base-n1', name: '强力除螨' }]
};

export default function PrimeDataFusionBoard({ projectId, onComplete, initialCategories = [], initialNeeds = [], onBack }) {
  // 状态：'selection' (选择模式) | 'fusion' (融合面板)
  const [viewMode, setViewMode] = useState('selection');
  const [loadingSocial, setLoadingSocial] = useState(false);
  const [hasSocialData, setHasSocialData] = useState(false);
  
  // 弹药库状态（右侧面板）
  const [arsenal, setArsenal] = useState(INITIAL_ARSENAL);
  
  // 提纯池状态（左侧面板，过滤掉已添加的）
  const [socialPool, setSocialPool] = useState({ categories: [], needs: [] });

  React.useEffect(() => {
    if (viewMode === 'fusion') {
      const fetchSocialData = async () => {
        setLoadingSocial(true);
        try {
          // If no projectId, fallback to mock (for UI preview during setup wizard before creation)
          if (!projectId) {
            setTimeout(() => {
              setSocialPool(MOCK_SOCIAL_DATA);
              setHasSocialData(true);
              setLoadingSocial(false);
            }, 800);
            return;
          }
          const res = await fetch(`${API_URL}/api/expansionTasks/project/${projectId}/distilled`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const data = await res.json();
          if (data.categories?.length > 0 || data.needs?.length > 0) {
            setSocialPool({ categories: data.categories || [], needs: data.needs || [] });
            setHasSocialData(true);
          } else {
            // No real data yet, fallback to mock for preview or show empty
            setSocialPool(MOCK_SOCIAL_DATA);
            setHasSocialData(true);
          }
        } catch (error) {
          console.error('Failed to fetch social data', error);
          setSocialPool(MOCK_SOCIAL_DATA);
        } finally {
          setLoadingSocial(false);
        }
      };
      fetchSocialData();
    }
  }, [viewMode, projectId]);

  // 将社媒数据注入弹药库
  const handleInject = (type, item) => {
    setArsenal(prev => ({ ...prev, [type]: [...prev[type], item] }));
    setSocialPool(prev => ({
      ...prev,
      [type]: prev[type].filter(i => i.id !== item.id)
    }));
  };

  // 从弹药库移除并退回社媒池
  const handleRemove = (type, item) => {
    setArsenal(prev => ({
      ...prev,
      [type]: prev[type].filter(i => i.id !== item.id)
    }));
    // 如果是基础AI生成的，就不退回社媒池了
    if (!item.id.startsWith('base-')) {
      setSocialPool(prev => ({ ...prev, [type]: [...prev[type], item] }));
    }
  };

  // 极速生成 (跳过注入)
  const handleFastTrack = () => {
    // 直接使用 INITIAL_ARSENAL 去生成 L1-L5
    onComplete(INITIAL_ARSENAL);
  };

  // 确认弹药库生成
  const handleConfirmFusion = () => {
    // 使用融合后的 arsenal 去生成 L1-L5
    onComplete(arsenal);
  };

  // 渲染：模式选择界面
  if (viewMode === 'selection') {
    return (
      <div className="w-full max-w-6xl mx-auto px-8 pb-8 pt-0 space-y-8 text-slate-200">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-white tracking-wide">构建意图弹药库</h2>
          <p className="text-slate-400 text-lg">
            基础维度已由 AI 初始化。您是否需要注入真实社媒洞察以强化长尾意图？
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {/* 卡片1：AI 极速模式 */}
          <div 
            onClick={handleFastTrack}
            className="group relative bg-slate-800/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-10 cursor-pointer hover:bg-slate-800/70 hover:border-slate-500 transition-all duration-300 flex flex-col items-center text-center space-y-6"
          >
            <div className="w-20 h-20 rounded-full bg-slate-700/50 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Zap className="w-10 h-10 text-slate-400 group-hover:text-amber-400" />
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-white mb-3">AI 极速预测模式</h3>
              <p className="text-slate-400 leading-relaxed">
                基于大模型行业常识，直接使用基础维度排列组合。<br/>适合宽泛行业的快速概览。
              </p>
            </div>
            <div className="mt-auto pt-6 flex items-center text-slate-300 font-medium">
              直接生成矩阵 <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </div>
          </div>

          {/* 卡片2：社媒提纯模式 (推荐) */}
          <div 
            onClick={() => setViewMode('fusion')}
            className="group relative bg-[#2996cf]/10 backdrop-blur-xl border border-[#2996cf]/30 rounded-2xl p-10 cursor-pointer hover:bg-[#2996cf]/20 hover:border-[#2996cf]/60 transition-all duration-300 flex flex-col items-center text-center space-y-6 shadow-[0_0_40px_-15px_rgba(41,150,207,0.3)]"
          >
            <div className="absolute top-4 right-4 bg-[#f43f5e] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
              推荐 RECOMMEND
            </div>
            <div className="w-20 h-20 rounded-full bg-[#2996cf]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Database className="w-10 h-10 text-[#2996cf]" />
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-white mb-3">全域社媒提纯模式</h3>
              <p className="text-slate-400 leading-relaxed">
                引入内部团队提纯的小红书、百度真实搜商数据。<br/>深度强化“品类”与“需求”维度，挖掘高价值蓝海意图。
              </p>
            </div>
            <div className="mt-auto pt-6 flex items-center text-[#2996cf] font-medium">
              进入工作台 <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </div>
          </div>
              </div>
        {onBack && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={onBack}
              className="px-6 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-700 text-slate-300 hover:text-white transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-slate-700/30"
            >
              <ArrowLeft className="w-4 h-4" /> 返回上一步
            </button>
          </div>
        )}
      </div>
    );
  }

  // 渲染：数据融合工作台 (双栏UI)
  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6 text-slate-200">
      {/* 头部 */}
      <div className="flex justify-between items-center pb-4 border-b border-slate-700/50">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Database className="w-6 h-6 text-[#2996cf]" /> 意图弹药库融合中心
          </h2>
          <p className="text-slate-400 text-sm mt-1">从左侧真实社媒池中提取价值标签，充实您的最终弹药库</p>
        </div>
        <button 
          onClick={() => setViewMode('selection')}
          className="px-4 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 text-slate-300 transition-colors"
        >
          返回选择
        </button>
      </div>

      {/* 左右分栏工作区 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[600px]">
        
        {/* 左栏：社媒提纯池 */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl flex flex-col overflow-hidden">
          <div className="p-4 bg-slate-800/80 border-b border-slate-700/50 flex items-center justify-between">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" /> 待确认真实洞察
            </h3>
            <span className="text-xs bg-slate-700 px-2 py-1 rounded text-slate-300">来源: 小红书 / 百度</span>
          </div>
          
          <div className="p-6 overflow-y-auto flex-1 space-y-8">
            {/* 需求池区块 */}
            <div>
              <h4 className="text-slate-400 mb-4 text-sm font-medium uppercase tracking-wider">长尾需求 (Needs)</h4>
              <div className="flex flex-wrap gap-3">
                {socialPool.needs.map(item => (
                  <div key={item.id} className="group flex items-center bg-slate-700/30 hover:bg-slate-700/60 border border-slate-600/50 rounded-lg pl-3 pr-1 py-1.5 transition-colors cursor-pointer" onClick={() => handleInject('needs', item)}>
                    <span className="mr-2 text-xs">
                      {item.source === 'xiaohongshu' ? '🔴' : '🔵'}
                    </span>
                    <span className="text-sm font-medium">{item.name}</span>
                    <div className="ml-3 p-1 rounded-md text-slate-400 group-hover:text-[#2996cf] group-hover:bg-[#2996cf]/10">
                      <Plus className="w-4 h-4" />
                    </div>
                  </div>
                ))}
                {socialPool.needs.length === 0 && (
                  <div className="text-slate-500 text-sm italic">所有提纯需求已全部注入</div>
                )}
              </div>
            </div>

            {/* 品类池区块 */}
            <div>
              <h4 className="text-slate-400 mb-4 text-sm font-medium uppercase tracking-wider">细分品类 (Categories)</h4>
              <div className="flex flex-wrap gap-3">
                {socialPool.categories.map(item => (
                   <div key={item.id} className="group flex items-center bg-slate-700/30 hover:bg-slate-700/60 border border-slate-600/50 rounded-lg pl-3 pr-1 py-1.5 transition-colors cursor-pointer" onClick={() => handleInject('categories', item)}>
                   <span className="mr-2 text-xs">
                     {item.source === 'xiaohongshu' ? '🔴' : '🔵'}
                   </span>
                   <span className="text-sm font-medium">{item.name}</span>
                   <div className="ml-3 p-1 rounded-md text-slate-400 group-hover:text-[#2996cf] group-hover:bg-[#2996cf]/10">
                     <Plus className="w-4 h-4" />
                   </div>
                 </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 右栏：我的弹药库 */}
        <div className="bg-[#2996cf]/5 border border-[#2996cf]/20 rounded-xl flex flex-col overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-b from-[#2996cf]/5 to-transparent pointer-events-none" />
          <div className="p-4 border-b border-[#2996cf]/20 flex items-center justify-between relative z-10">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#2996cf]" /> 最终意图弹药库
            </h3>
            <span className="text-[#2996cf] text-sm font-medium">Ready for Matrix</span>
          </div>

          <div className="p-6 overflow-y-auto flex-1 space-y-8 relative z-10">
            {/* 弹药库：需求 */}
            <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-5 w-full">
              <h4 className="text-slate-300 mb-4 font-medium flex justify-between">
                <span>需求维度 (Needs)</span>
                <span className="text-slate-500 text-sm">{arsenal.needs.length} 个</span>
              </h4>
              <div className="flex flex-wrap gap-2 min-h-[60px] content-start">
                {arsenal.needs.map(item => (
                  <div key={item.id} className="flex items-center bg-[#2996cf]/20 border border-[#2996cf]/40 text-blue-100 rounded-lg pl-3 pr-1 py-1">
                    {item.source && <span className="mr-1 text-[10px]">{item.source === 'xiaohongshu' ? '🔴' : '🔵'}</span>}
                    <span className="text-sm">{item.name}</span>
                    <button onClick={() => handleRemove('needs', item)} className="ml-2 p-1 rounded hover:bg-[#2996cf]/30 text-blue-300 hover:text-white transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* 弹药库：品类 */}
            <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-5 w-full">
              <h4 className="text-slate-300 mb-4 font-medium flex justify-between">
                <span>品类维度 (Categories)</span>
                <span className="text-slate-500 text-sm">{arsenal.categories.length} 个</span>
              </h4>
              <div className="flex flex-wrap gap-2 min-h-[60px] content-start">
                {arsenal.categories.map(item => (
                  <div key={item.id} className="flex items-center bg-[#2996cf]/20 border border-[#2996cf]/40 text-blue-100 rounded-lg pl-3 pr-1 py-1">
                    {item.source && <span className="mr-1 text-[10px]">{item.source === 'xiaohongshu' ? '🔴' : '🔵'}</span>}
                    <span className="text-sm">{item.name}</span>
                    <button onClick={() => handleRemove('categories', item)} className="ml-2 p-1 rounded hover:bg-[#2996cf]/30 text-blue-300 hover:text-white transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 底部确认操作条 */}
      <div className="mt-8 flex justify-end">
        <button 
          onClick={handleConfirmFusion}
          className="bg-gradient-to-r from-[#2996cf] to-[#1e76a6] hover:from-[#207eb3] hover:to-[#176087] text-white px-10 py-4 rounded-xl font-bold text-lg shadow-[0_0_30px_-5px_rgba(41,150,207,0.5)] hover:shadow-[0_0_40px_0_rgba(41,150,207,0.7)] transition-all flex items-center gap-3 transform hover:-translate-y-1 w-full md:w-auto justify-center"
        >
          确认弹药库配置，生成 L1-L5 矩阵 <ArrowRight className="w-6 h-6" />
        </button>
            </div>
        {onBack && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={onBack}
              className="px-6 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-700 text-slate-300 hover:text-white transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-slate-700/30"
            >
              <ArrowLeft className="w-4 h-4" /> 返回上一步
            </button>
          </div>
        )}
      </div>
    );
  }
